import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, type Variants } from '@/components/framer-motion'
import { Button } from '@/components/ui/button'

const fadeUpVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.23, 1, 0.32, 1],
    },
  },
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
      duration: 0.9,
      ease: [0.23, 1, 0.32, 1],
    },
  },
}

const numberVariants: Variants = {
  hidden: {
    opacity: 0,
    y: -170,
    scale: 1,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      damping: 30,
      stiffness: 350,
      mass: 3,
    },
  },
}

export function NotFoundPage() {
  const [isHovering, setIsHovering] = useState(false)
  const [gradientPosition, setGradientPosition] = useState({ x: 50, y: 50 })

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth) * 100
      const y = (e.clientY / window.innerHeight) * 100
      setGradientPosition({ x, y })
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  return (
    <div className="h-screen overflow-hidden bg-black relative">
      {/* Animated background gradient overlay */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${gradientPosition.x}% ${gradientPosition.y}%, rgba(37, 133, 104, 0.3) 0%, transparent 60%)`,
          transition: 'background 0.5s ease',
        }}
      />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="h-full relative z-10"
      >
        <div className="h-full w-full flex flex-col items-center justify-center">
          {/* Logo and page header */}
          <motion.div
            variants={fadeUpVariants}
            className="absolute top-8 left-1/2 transform -translate-x-1/2"
          >
            <div className="md:flex md:items-center gap-4 group">
              <Link to="/" className="flex justify-center gap-4">
                <img
                  src="/assets/images/super-logo.svg"
                  alt="Super Logo"
                  className="h-6 w-auto invert opacity-80 group-hover:opacity-100 transition-opacity"
                />
              </Link>
              <h1
                className="mt-4 md:mt-0 text-sm font-bold text-gray-100/60 uppercase"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Page Not Found
              </h1>
            </div>
          </motion.div>

          {/* Main content */}
          <div className="max-w-2xl w-full space-y-8 text-center px-4">
            {/* Animated 404 */}
            <motion.div variants={numberVariants} className="relative">
              {/* Main 404 text with gradient animation */}
              <h1
                className="relative text-[120px] sm:text-[160px] md:text-[240px] font-black leading-none select-none"
                style={{
                  background: `linear-gradient(135deg, #258568 0%, #2a9173 25%, #a6eeeb 50%, #258568 75%, #2a9173 100%)`,
                  backgroundSize: '200% 200%',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'gradient-shift 8s ease infinite',
                  letterSpacing: '-8px',
                }}
              >
                404
              </h1>

              {/* GIF overlay on top of gradient text */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: `url(/assets/images/ethereal.gif)`,
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: '0% 27%',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  opacity: 0.3,
                  mixBlendMode: 'screen',
                }}
              >
                <span
                  className="text-[120px] sm:text-[160px] md:text-[240px] font-black leading-none select-none"
                  style={{ letterSpacing: '-8px' }}
                >
                  404
                </span>
              </div>
            </motion.div>

            {/* Description - Made bigger */}
            <motion.div variants={fadeUpVariants} className="space-y-6">
              <p
                className="text-lg md:text-2xl text-gray-100 max-w-lg mx-auto"
                style={{ fontFamily: 'Fragment Mono, monospace' }}
              >
                The page you are looking for doesn&apos;t exist or has been
                moved. Please go back to the home page.
              </p>
            </motion.div>

            {/* Button - matching home page exactly */}
            <motion.div variants={fadeUpVariants} className="w-full space-y-4">
              <motion.div
                whileTap={{ scale: 0.98 }}
                className="w-full flex justify-center"
              >
                <motion.div
                  className="w-full max-w-[14rem] rounded-sm"
                  animate={{
                    background: isHovering
                      ? 'linear-gradient(to right, #258568, #2a9173)'
                      : 'linear-gradient(to right,rgb(16, 16, 16),rgb(17, 17, 17))',
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <Button
                    asChild
                    className="w-full bg-transparent hover:bg-transparent text-lg"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                    size="lg"
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                  >
                    <Link to="/">Go to home page</Link>
                  </Button>
                </motion.div>
              </motion.div>

              <motion.div variants={fadeUpVariants}>
                <Link
                  to="/sign-in"
                  className="text-base text-gray-300 hover:text-white transition-colors underline"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  or Sign in
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
